import { useEffect, useRef, useState } from 'react';
import type { FirstRoundEntry } from '@ff/core';
import { fetchLineups, fetchOverrides, saveOverrides } from '../lib/api.ts';
import { useCurrentSeason } from '../lib/currentSeason.tsx';
import { points } from '../lib/format.ts';

/**
 * Who a draft award can actually be won by.
 *
 * The pot punishes your own draft mistake, so trading that mistake away
 * shouldn't win it for you — but ESPN publishes per-team trade counts and no
 * per-player transaction history, which means a first-rounder sitting on
 * somebody else's roster in December was either traded or dropped and claimed
 * off waivers. Those mean opposite things and the API cannot tell them apart.
 *
 * So the app does the part it can do honestly: flag every first-round pick that
 * is no longer on the drafter's roster, and let the commissioner rule.
 */
export function AwardEligibility({
  season,
  awardId,
  label,
  amount,
  pick,
  weeks,
  firstRound,
  rulings,
  nameOf,
}: {
  season: number;
  /** The award these rulings apply to. */
  awardId: string;
  /** The league's own name for it. */
  label: string;
  /** What it pays, so the copy says the real number. */
  amount: number;
  /**
   * Which end of the board wins it. The resolver and the award page both
   * honour this, so the page used to MAKE the rulings had better agree.
   */
  pick: 'low' | 'high';
  /** Weeks to look for rosters in, newest first. */
  weeks: number[];
  firstRound: FirstRoundEntry[];
  /** Every award's rulings, award id -> owner ids ruled out. */
  rulings: Record<string, string[]>;
  nameOf: (id: string) => string;
}) {
  const [excluded, setExcluded] = useState<string[]>(rulings[awardId] ?? []);
  const [heldBy, setHeldBy] = useState<Record<number, string | null> | null>(null);
  const [rosterError, setRosterError] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'stale' | 'error'>(
    'idle',
  );
  /**
   * Saves run one at a time, in click order.
   *
   * Every save replaces the whole list, so two in flight can commit out of
   * order: uncheck A then B, B's request lands first, and A's stale array wins
   * — leaving B eligible while the page says otherwise. That silently moves $25
   * to the wrong manager, so each save waits for the one before it.
   */
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  /**
   * The last set the server confirmed.
   *
   * Rolling a failure back to the value this mounted with would undo edits that
   * already committed: exclude A successfully, fail to exclude B, and the page
   * would show A included again — then the next save would write that back and
   * erase A for real.
   */
  const committed = useRef<string[]>(rulings[awardId] ?? []);
  /**
   * Which edit is the newest. A rollback is only ever safe for the last thing
   * the commissioner clicked: undoing a failed first edit while a second is
   * still queued would replace the state that second edit is about to confirm,
   * and the UI would end up behind the server with no way to notice.
   */
  const latest = useRef(0);
  /**
   * The pending "back to idle" timer.
   *
   * An older save finishing successfully must not clear the status of a newer
   * one — otherwise a failure that lands within two seconds of an earlier
   * success is silently wiped, and the commissioner is left believing a ruling
   * stuck when it didn't.
   */
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { refresh } = useCurrentSeason();

  useEffect(() => () => clearTimeout(idleTimer.current), []);

  // Who currently rosters each first-round pick, from the newest lineups we
  // have. Absent from every roster means they're a free agent.
  useEffect(() => {
    let mounted = true;
    setRosterError(false);
    (async () => {
      // Newest first, because between a finished game and the next kickoff the
      // scoreboard's week is the OLD one — and a player traded on the Tuesday
      // only shows as moved in the upcoming week's rosters, which is exactly
      // when this prompt matters.
      let failed = false;
      for (const week of weeks) {
        const lineups = await fetchLineups(season, week).catch(() => {
          // A failure is not "nobody moved" — saying nothing here would read as
          // an all-clear on the page where the ruling gets made.
          failed = true;
          return null;
        });
        if (!mounted) return;
        if (!lineups) continue;
        const owner: Record<number, string | null> = {};
        for (const entry of firstRound) owner[entry.playerId] = null;
        for (const team of lineups.teams) {
          for (const p of [...team.starters, ...team.bench]) {
            if (p.playerId in owner) owner[p.playerId] = team.ownerId;
          }
        }
        setHeldBy(owner);
        // Falling back to an older week is not an all-clear: a trade made since
        // then can only show in the newer rosters we failed to read.
        if (failed) setRosterError(true);
        return;
      }
      setHeldBy(null);
      if (failed) setRosterError(true);
    })();
    return () => {
      mounted = false;
    };
  }, [season, weeks, firstRound]);

  function save(next: string[]) {
    const mine = (latest.current += 1);
    setExcluded(next);
    setStatus('saving');
    queue.current = queue.current
      .catch(() => {})
      .then(async () => {
        try {
          // Just this award. The server merges it into whatever it holds,
          // inside the transaction — posting the whole map from here could not
          // be made safe, because a sibling panel can always have changed it
          // between the read and the write.
          const { rebuilt } = await saveOverrides(season, {
            ineligibleFor: { awardId, ownerIds: next },
          });
          committed.current = next;
          // Show what actually committed, so the UI can't drift behind a save
          // that succeeded while an earlier one failed.
          if (mine === latest.current) setExcluded(next);
          // The API rebuilds the money board on save, so the bundle this app is
          // holding is now stale — and /clown reads the crown off it. Without
          // this, navigating there could grey somebody out and crown them.
          const refreshed = await refresh();
          // Only the newest edit gets to speak for the form's status.
          if (mine !== latest.current) return;
          setStatus(rebuilt && refreshed ? 'saved' : 'stale');
          if (rebuilt && refreshed) {
            clearTimeout(idleTimer.current);
            idleTimer.current = setTimeout(() => setStatus('idle'), 2000);
          }
        } catch {
          // A newer click is already queued: let it settle both the state and
          // what the form says about it, rather than reporting over the top.
          if (mine !== latest.current) return;
          clearTimeout(idleTimer.current);
          setStatus('error');
          // A rejected PUT does NOT mean nothing was written: the route commits
          // the override before it rebuilds the board. Ask the server what it
          // actually holds rather than assuming, or the next edit would submit a
          // stale array and erase an exclusion that did land.
          const actual = await fetchOverrides(season)
            .then((o) => {
              const { [awardId]: mine2 } = o.ineligible ?? {};
              return mine2 ?? [];
            })
            .catch(() => committed.current);
          committed.current = actual;
          setExcluded(actual);
        }
      });
  }

  const board = [...firstRound].sort((a, b) => a.pick - b.pick);

  return (
    <section className="space-y-3">
      <h2 className="eyebrow">{label} eligibility</h2>
      <p className="text-sm text-chalk-dim">
        Uncheck anyone whose first-round pick shouldn't count — traded him away,
        or whatever the league decides. The ${amount} goes to the{' '}
        {pick === 'low' ? 'lowest' : 'highest'} total among the ones still
        checked. ESPN can't tell a trade from a waiver claim, so the flag below
        is a prompt, not a verdict.
      </p>

      <div className="card divide-y divide-felt-line p-1">
        {board.map((e) => {
          const counts = !excluded.includes(e.ownerId);
          const holder = heldBy?.[e.playerId];
          // Undefined means we haven't loaded rosters; null means nobody has him.
          const moved = heldBy !== null && holder !== undefined && holder !== e.ownerId;
          return (
            <label
              key={e.ownerId}
              className="flex cursor-pointer items-center gap-3 px-3 py-2"
            >
              <input
                type="checkbox"
                checked={counts}
                onChange={(ev) =>
                  save(
                    ev.target.checked
                      ? excluded.filter((id) => id !== e.ownerId)
                      : [...excluded, e.ownerId],
                  )
                }
                className="h-4 w-4 shrink-0 accent-brass"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-display text-sm font-bold">
                    {nameOf(e.ownerId)}
                  </span>
                  {moved && (
                    <span className="font-mono text-[0.6rem] uppercase tracking-wider text-brass">
                      {holder === null
                        ? 'dropped — free agent'
                        : `now on ${nameOf(holder)}'s roster`}
                    </span>
                  )}
                </div>
                <div className="truncate font-mono text-[0.65rem] text-chalk-dim">
                  Pick {e.pick} · {e.playerName} ({e.position})
                </div>
              </div>
              <span className="shrink-0 font-mono text-sm text-chalk-dim tabular">
                {points(e.total)}
              </span>
            </label>
          );
        })}
      </div>

      {rosterError && (
        <p className="font-mono text-xs text-brass">
          Couldn't read the newest rosters, so anything below that isn't flagged
          as moved might simply not have been checked.
        </p>
      )}
      {status === 'saving' && <p className="font-mono text-xs text-chalk-dim">saving…</p>}
      {status === 'saved' && (
        <p className="font-mono text-xs text-cash">saved. the board is rebuilt.</p>
      )}
      {status === 'stale' && (
        <p className="font-mono text-xs text-brass">
          Saved, but the money board didn't come back — reload before trusting
          who's crowned on the {label} page.
        </p>
      )}
      {status === 'error' && <p className="font-mono text-xs text-blood">Could not save that.</p>}
    </section>
  );
}
